import React, {useState, useEffect, useMemo, useCallback} from "react";
import {
  Main,
  Timeline,
  Expertise,
  Project,
  Contact,
  Navigation,
  Footer,
} from "./components";
import FadeIn from './components/FadeIn';
import { useStreamingChat, ChatMessage } from './hooks/useStreamingChat';
import { trackScrollDepth, trackChatMessage } from './utils/analytics';
import './index.scss';

function App() {
    const [mode, setMode] = useState<string>('dark');

    const handleModeChange = () => {
        if (mode === 'dark') {
            setMode('light');
        } else {
            setMode('dark');
        }
    }

    // Chatbot state - using streaming hook
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(true);

    const initialMessage: ChatMessage = {
        id: '1',
        content: "Hi! I'm Yaniv's AI assistant. I can tell you about his background, projects, or answer questions about his work. What would you like to know?",
        role: 'assistant',
        timestamp: new Date()
    };

    const {
        messages: chatMessages,
        setMessages: setChatMessages,
        sendMessage,
        isStreaming,
        setProjectContext: setCurrentProjectContext
    } = useStreamingChat({ initialMessages: [initialMessage] });

    // Alias isStreaming to isTyping for backward compatibility with Contact component
    const isTyping = isStreaming;
    // Terminal-log-style status lines shown while the assistant works,
    // rotated per question so repeat use doesn't feel static.
    const loadingMessageSets = useMemo(() => [
        [
            "parsing question",
            "reading project files",
            "cross-referencing sources",
            "drafting response"
        ],
        [
            "reading question",
            "searching documentation",
            "checking details",
            "writing response"
        ],
        [
            "parsing input",
            "scanning archives",
            "gathering specifics",
            "composing reply"
        ],
        [
            "reading query",
            "querying documentation",
            "extracting details",
            "finalizing response"
        ]
    ], []);

    const [questionCount, setQuestionCount] = useState(0);
    const [currentLoadingMessage, setCurrentLoadingMessage] = useState<string>('');

    // Get the current loading message set based on question count
    const loadingMessages = loadingMessageSets[questionCount % loadingMessageSets.length];


    // Cycle through loading messages only once while AI is thinking
    useEffect(() => {
        let messageInterval: NodeJS.Timeout;
        let messageIndex = 0;

        if (isStreaming && loadingMessages.length > 0) {
            setCurrentLoadingMessage(loadingMessages[0]);
            messageInterval = setInterval(() => {
                messageIndex++;
                if (messageIndex < loadingMessages.length) {
                    setCurrentLoadingMessage(loadingMessages[messageIndex]);
                } else {
                    // Stop the interval when we've gone through all messages once
                    clearInterval(messageInterval);
                }
            }, 2000); // Change message every 2 seconds for better pacing
        } else {
            setCurrentLoadingMessage('');
        }

        return () => {
            if (messageInterval) {
                clearInterval(messageInterval);
            }
        };
    }, [isStreaming, loadingMessages]);

    const suggestedQuestions = [
        "What are Yaniv's main skills?",
        "Tell me about his education",
        "What kind of roles or companies is Yaniv interested in?",
        "What are some of Yaniv's favorite side projects?",
        "What's Yaniv's most unexpected hobby?",
        "If Yaniv could build any AI-powered product, what would it be?",
        "How can I contact Yaniv?"
    ];

    // Generate detailed project introduction message
    const getProjectIntroductionMessage = (projectName: string) => {
        if (projectName === 'general') {
            return `Hi! I'm Yaniv's AI assistant. I can tell you about his background, projects, or answer questions about his work. What would you like to know?`;
        }
        // Only mention confidentiality for specific projects
        const confidentialProjects = [
            'Kalff Fundraising Dashboard',
            'VibeGroup Recruitment Algorithm',
            'KPN Easy Mode'
        ];

        const base = `Hi! I'm Yaniv's AI assistant. I can tell you about the ${projectName} project.`;

        const confidentiality = confidentialProjects.includes(projectName)
            ? " Some details are confidential, but I can share technical highlights."
            : '';

        return `${base}${confidentiality} Here are some questions you can ask:\n\n• What tech stack was used?\n• What problem does this solve?\n• What was Yaniv's favorite part?\n• What challenges were overcome?`;
    };

    const openChatWithMessage = useCallback((message: string, projectName?: string) => {
        setIsChatOpen(true);

        // Increment question count to rotate loading message sets
        setQuestionCount(prev => prev + 1);

        // Set project context if provided
        if (projectName) {
            setCurrentProjectContext(projectName);
        }

        setShowSuggestions(false);

        // Add user message
        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            content: message,
            role: 'user',
            timestamp: new Date()
        };

        // Show default introduction message instead of calling OpenAI immediately
        const aiMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            content: getProjectIntroductionMessage(projectName || 'general'),
            role: 'assistant',
            timestamp: new Date(),
            followUpQuestions: [
                "What tech stack was used?",
                "What problem does this solve?",
                "What was Yaniv's favorite part?",
                "What challenges were overcome?"
            ],
            interactiveElement: { type: 'text', content: '' }
        };

        setChatMessages(prev => [...prev, userMessage, aiMessage]);
    }, [setCurrentProjectContext, setChatMessages]);

    // Scroll depth tracking
    useEffect(() => {
        const scrollFlags = { scrolled25: false, scrolled50: false, scrolled75: false };
        
        const handleScroll = () => {
            const scrolled = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;

            if (scrolled > 25 && !scrollFlags.scrolled25) {
                trackScrollDepth(25);
                scrollFlags.scrolled25 = true;
            }
            if (scrolled > 50 && !scrollFlags.scrolled50) {
                trackScrollDepth(50);
                scrollFlags.scrolled50 = true;
            }
            if (scrolled > 75 && !scrollFlags.scrolled75) {
                trackScrollDepth(75);
                scrollFlags.scrolled75 = true;
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSendMessage = useCallback(async (message: string) => {
        if (message.trim() && !isStreaming) {
            // Track chat message
            trackChatMessage(message.trim());
            
            // Increment question count to rotate loading message sets
            setQuestionCount(prev => prev + 1);
            setChatInput('');
            setShowSuggestions(false);

            // Use the streaming hook's sendMessage - it handles everything
            await sendMessage(message.trim());
        }
    }, [isStreaming, sendMessage]);

    return (
    <div className={`main-container ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
        <Navigation parentToChild={{mode}} modeChange={handleModeChange}/>
        <FadeIn transitionDuration={700}>
            <Main/>
            <Expertise/>
            <Timeline/>
            <Project parentToChild={{mode}} openChatWithMessage={openChatWithMessage}/>
            <Contact
                parentToChild={{mode}}
                chatProps={{
                    isChatOpen,
                    setIsChatOpen,
                    chatMessages,
                    setChatMessages,
                    chatInput,
                    setChatInput,
                    isTyping,
                    showSuggestions,
                    setShowSuggestions,
                    suggestedQuestions,
                    currentLoadingMessage,
                    handleSuggestionClick: async (question: string) => {
                        await handleSendMessage(question);
                    },
                    handleSendMessage
                }}
            />
        </FadeIn>
        <Footer />
    </div>
    );
}

export default App;
